import { computed, Injectable, Signal, signal } from '@angular/core';
import { fromEvent, tap, throttleTime } from 'rxjs';

import { Size, ValueOf } from '../../types/base';
import { type Breakpoint, breakpoints, layoutCutoff } from './tokens';
export { breakpoints, Breakpoint } from './tokens';

export const layouts = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile',
} as const;
export type Layout = ValueOf<typeof layouts>;

export type MediaQueryOptions = {
  /** exclude current value of the range */
  exclude: boolean;
  /** return signal based test */
  signal: false | boolean;
};

@Injectable({
  providedIn: 'root',
})
export class ResponsiveService {
  viewport = signal<Size>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  isResizing = signal(false);

  constructor() {
    this.#handleResize();
  }

  layout = computed((): Layout => {
    const aboveCutoff = this.above(layoutCutoff, { signal: true })();

    return aboveCutoff ? layouts.DESKTOP : layouts.MOBILE;
  });

  isMobile = computed(() => this.layout() === layouts.MOBILE);
  isDesktop = computed(() => this.layout() === layouts.DESKTOP);

  above(
    breakpoint: Breakpoint | number,
    options: Partial<MediaQueryOptions> & { signal: true },
  ): Signal<boolean>;
  above(
    breakpoint: Breakpoint | number,
    options?: Partial<Omit<MediaQueryOptions, 'signal'>> | { signal?: false },
  ): boolean;
  above(
    breakpoint: Breakpoint | number,
    options: Partial<MediaQueryOptions> = {},
  ): boolean | Signal<boolean> {
    const optionsWithDefaults: MediaQueryOptions = {
      exclude: false,
      signal: false,
      ...options,
    };
    const { exclude, signal: useSignal } = optionsWithDefaults;

    const targetWidth =
      (typeof breakpoint === 'number' ? breakpoint : breakpoints[breakpoint]) + (exclude ? 1 : 0);

    const test = () => this.viewport().width >= targetWidth;

    return useSignal ? computed(test) : test();
  }

  below(
    breakpoint: Breakpoint | number,
    options: Partial<MediaQueryOptions> & { signal: true },
  ): Signal<boolean>;
  below(
    breakpoint: Breakpoint | number,
    options?: Partial<Omit<MediaQueryOptions, 'signal'>> | { signal?: false },
  ): boolean;
  below(
    breakpoint: Breakpoint | number,
    options: Partial<MediaQueryOptions> = {},
  ): boolean | Signal<boolean> {
    const optionsWithDefaults: MediaQueryOptions = {
      exclude: true,
      signal: false,
      ...options,
    };
    const { exclude, signal: useSignal } = optionsWithDefaults;

    const targetWidth =
      (typeof breakpoint === 'number' ? breakpoint : breakpoints[breakpoint]) + (exclude ? -1 : 0);

    const test = () => this.viewport().width <= targetWidth;

    return useSignal ? computed(test) : test();
  }

  between(
    lower: Breakpoint | number,
    upper: Breakpoint | number,
    options: Partial<MediaQueryOptions> & { signal: true },
  ): Signal<boolean>;
  between(
    lower: Breakpoint | number,
    upper: Breakpoint | number,
    options?: Partial<Omit<MediaQueryOptions, 'signal'>> | { signal?: false },
  ): boolean;
  between(
    lower: Breakpoint | number,
    upper: Breakpoint | number,
    options: Partial<MediaQueryOptions> = {},
  ): boolean | Signal<boolean> {
    const test = () =>
      (this.above(lower, { exclude: false }) as boolean) &&
      (this.below(upper, { exclude: true }) as boolean);

    return options.signal ? computed(test) : test();
  }

  #handleResize(): void {
    let resetIsResizingTimeout: number;

    fromEvent(window, 'resize')
      .pipe(
        tap(() => {
          if (this.isResizing()) return;
          this.isResizing.set(true);
          if (resetIsResizingTimeout) clearTimeout(resetIsResizingTimeout);
        }),
        throttleTime(200, undefined, { leading: false, trailing: true }),
        tap(() => {
          resetIsResizingTimeout = setTimeout(() => {
            this.isResizing.set(false);
          }, 200) as unknown as number;
        }),
        tap((_event: Event) => {
          this.viewport.set({
            width: window.innerWidth,
            height: window.innerHeight,
          });
        }),
      )
      .subscribe();
  }
}
